/* EventiListPage.jsx — Pagina pubblica "Eventi Colucci in Programma".
 *
 * Lista filtri base (Tutti / Futuri / In corso / Passati) + card per evento.
 * Mobile-first. Backend: GET /api/public/eventi/in-programma
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Calendar, MapPin, ArrowRight, Clock, Sparkles,
  Loader2, CalendarRange, Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/eventi`;

const FILTERS = [
  { key: "tutti", label: "Tutti" },
  { key: "futuro", label: "Prossimi" },
  { key: "in_corso", label: "In corso" },
];

export default function EventiListPage() {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutti");

  useEffect(() => {
    axios.get(`${API}/in-programma?limit=50`)
      .then((r) => setEventi(r.data.items || []))
      .catch(() => setEventi([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = eventi.filter((e) =>
    filter === "tutti" ? true : e.status_label === filter,
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1 pb-16">
        {/* HERO */}
        <section className="relative bg-gradient-to-br from-[#163321] via-[#1d4a2e] to-[#D97B58]/40 text-white py-16 sm:py-20 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-amber-300/10 blur-3xl translate-x-32 -translate-y-32" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-bold mb-4">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              Eventi Colucci
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4 drop-shadow"
              data-testid="eventi-page-title"
            >
              Eventi in Programma
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed">
              Vieni a trovarci nei nostri eventi: consulenze gratuite,
              workshop, concorsi e occasioni di networking sul territorio.
            </p>
          </div>
        </section>

        {/* FILTERS */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-wrap gap-2" data-testid="eventi-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`eventi-filter-${f.key}`}
                className={[
                  "min-h-[44px] px-5 py-2.5 rounded-full text-sm font-bold border-2 transition-all active:scale-95",
                  filter === f.key
                    ? "bg-[#163321] text-white border-[#163321] shadow-md"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#163321] hover:text-[#163321]",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
          </div>
        </section>

        {/* LIST */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#163321]" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState filter={filter} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
                 data-testid="eventi-list-grid">
              {filtered.map((ev) => (
                <EventoCard key={ev.slug} ev={ev} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function EventoCard({ ev }) {
  const statusLabel = {
    futuro: { text: "Prossimo", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    in_corso: { text: "In corso", color: "bg-red-100 text-red-700 border-red-200 animate-pulse" },
    passato: { text: "Passato", color: "bg-slate-100 text-slate-500 border-slate-200" },
  }[ev.status_label] || { text: "Evento", color: "bg-slate-100 text-slate-600 border-slate-200" };

  return (
    <Link
      to={`/eventi/${ev.slug}`}
      data-testid={`evento-card-${ev.slug}`}
      className="group bg-white rounded-3xl border-2 border-slate-200 overflow-hidden hover:border-[#163321] hover:shadow-2xl transition-all flex flex-col"
    >
      {/* Thumbnail locandina (o fallback gradiente) */}
      <div
        className="relative h-44 sm:h-52 overflow-hidden"
        style={ev.locandina_url
          ? { backgroundImage: `url(${ev.locandina_url})`,
              backgroundSize: "cover", backgroundPosition: "center" }
          : { background: `linear-gradient(135deg, ${ev.banner_color || "#D97B58"} 0%, #163321 100%)` }}
      >
        {!ev.locandina_url && (
          <div className="absolute inset-0 flex items-center justify-center text-7xl opacity-20">
            🎪
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusLabel.color}`}>
            {statusLabel.text}
          </span>
        </div>
        {ev.incentivo && (
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-xs font-bold bg-amber-400 text-[#163321] inline-flex items-center gap-1 px-2.5 py-1 rounded-full">
              {ev.incentivo.replace(/^[\u{1F3C6}\u{1F389}\u{1F4E6}]\s*/u, "").substring(0, 50)}
            </p>
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-heading font-black text-lg sm:text-xl text-[#163321] mb-1 line-clamp-2 group-hover:text-[#1d4a2e]">
          {ev.titolo}
        </h3>
        {ev.sottotitolo && (
          <p className="text-sm text-slate-600 line-clamp-2 mb-3">
            {ev.sottotitolo}
          </p>
        )}

        <div className="space-y-1.5 text-sm text-slate-700 mt-auto">
          {ev.data_inizio && (
            <p className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#163321] shrink-0" aria-hidden="true" />
              {formatDateIT(ev.data_inizio)}
              {ev.data_fine && ev.data_fine !== ev.data_inizio && ` — ${formatDateIT(ev.data_fine)}`}
            </p>
          )}
          {ev.ora_inizio && (
            <p className="flex items-center gap-2 text-slate-500">
              <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
              {ev.ora_inizio}{ev.ora_fine && ` - ${ev.ora_fine}`}
            </p>
          )}
          {ev.luogo && (
            <p className="flex items-center gap-2 text-slate-500">
              <MapPin className="w-4 h-4 shrink-0" aria-hidden="true" />
              {ev.luogo}{ev.citta && `, ${ev.citta}`}
            </p>
          )}
        </div>

        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#163321] group-hover:gap-3 transition-all">
          {ev.status_label === "passato" ? "Scopri di piu'" : "Iscriviti"}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ filter }) {
  return (
    <div
      className="text-center py-16 px-4"
      data-testid="eventi-empty-state"
    >
      <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <CalendarRange className="w-8 h-8 text-slate-400" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-bold text-slate-700 mb-2">
        Nessun evento {filter !== "tutti" ? `${filter}` : "in programma"}
      </h2>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
        Torna a trovarci presto: pubblichiamo nuovi eventi ogni mese.
      </p>
      <Link
        to="/consulenza"
        className="inline-flex items-center gap-2 min-h-[48px] px-6 py-3 rounded-full bg-[#163321] text-white font-bold text-sm hover:bg-[#1d4a2e] transition-all"
      >
        <Building2 className="w-4 h-4" aria-hidden="true" />
        Prenota una consulenza
      </Link>
    </div>
  );
}

function formatDateIT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}
